import { Fragment } from 'react';
import { ChainID, ChainType } from '@ankr.com/chains-list';

import { ChainGroupID } from 'modules/endpoints/types';
import { useChainSelectors } from 'modules/chains/components/ChainSelectorsProvider';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { getEndpointName } from 'modules/endpoints/utils/getEndpointName';

import { Endpoint } from '../Endpoint';
import { EndpointsHeader } from '../EndpointsHeader';
import { MainEndpointsProps } from './types';
import { Placeholder } from './components/Placeholder';
import { useMainEndpoints } from './hooks/useMainEndpoints';
import { useMainEndpointsStyles } from './MainEndpointsStyles';
import { renderFlareTitle } from './utils/renderFlareTitle';
import { mainEndpointsTranslation } from './translation';
import { renderSuiMainnetTitle } from './utils/renderSuiMainnetTitle';

export const MainEndpoints = ({
  chainType,
  feature,
  group,
  publicChain,
}: MainEndpointsProps) => {
  const { flattenURLs, hasPlaceholder, shouldSignIn, title } = useMainEndpoints(
    {
      feature,
      group,
      publicChain,
      chainType,
    },
  );

  const { classes } = useMainEndpointsStyles();
  const { keys, t } = useTranslation(mainEndpointsTranslation);

  const headerTooltip =
    group.id === ChainGroupID.BTC_BLOCKBOOK
      ? t(keys.blockbookTooltip)
      : undefined;

  const endpointsHeader = (
    <EndpointsHeader title={title} tooltipText={headerTooltip} />
  );

  const { chainCallProtocol } = useChainSelectors();

  const isGRPC = chainCallProtocol === 'grpc';

  if (isGRPC || flattenURLs.length === 0) {
    return null;
  }

  if (hasPlaceholder) {
    return (
      <Placeholder
        publicChain={publicChain}
        shouldSignIn={shouldSignIn}
        title={endpointsHeader}
      />
    );
  }

  const isSuiMainnet =
    publicChain.id === ChainID.SUI && chainType === ChainType.Mainnet;

  const isFlareTestnetEndpoints =
    publicChain.id === ChainID.FLARE && chainType === ChainType.Testnet;

  const isCustomEndpoints =
    isFlareTestnetEndpoints || isSuiMainnet || group.id === ChainGroupID.MERGED;

  const getTitle = (url: string) => {
    if (isFlareTestnetEndpoints) return renderFlareTitle(url);

    if (isSuiMainnet) return renderSuiMainnetTitle(url);

    return getEndpointName(group.urls, url);
  };

  if (isCustomEndpoints) {
    return (
      <>
        {flattenURLs.map(url => (
          <div className={classes.root} key={url}>
            <EndpointsHeader title={getTitle(url)} />
            <Endpoint url={url} />
          </div>
        ))}
      </>
    );
  }

  return (
    <div className={classes.root}>
      {endpointsHeader}
      {flattenURLs.map(url => (
        <Fragment key={url}>
          <Endpoint url={url} />
        </Fragment>
      ))}
    </div>
  );
};
